// Debounced PATCH/POST/DELETE dispatcher for smart document blocks.
// Coalesces rapid property edits per blockId so we don't flood the
// backend during inline text edits. The returned promise resolves on
// the eventual server response; callers can await it or fire and
// forget.

import { updateBlock, insertBlock, deleteBlock, type DocumentBlock } from '@/api/documents'
import toast from 'react-hot-toast'

// Synthetic block id used to persist per-document state (vars, order,
// deleted). Mirrored from RuntimeContext to avoid the import cycle.
const DOC_STATE_BLOCK_ID = '__doc_state__'

type Pending = {
  timer: ReturnType<typeof setTimeout> | null
  latest: Record<string, unknown>
  // Outstanding caller promises waiting on the next flush of this slot.
  waiters: Array<{
    resolve: (b: DocumentBlock) => void
    reject: (err: unknown) => void
  }>
  inflight: Promise<DocumentBlock> | null
}

const DEBOUNCE_MS = 500

export interface SyncState {
  /** True when any patch is queued or in-flight. */
  pending: boolean
  /** Timestamp of the last successful save (any verb). */
  lastSavedAt: number | null
  /** Latest error message, if any save failed since the last success. */
  lastError: string | null
}

export class DocSync {
  private pending = new Map<string, Pending>()
  private listeners = new Set<(s: SyncState) => void>()
  private state: SyncState = { pending: false, lastSavedAt: null, lastError: null }

  constructor(private readonly docId: string) {}

  /** Subscribe to sync-state changes. Returns an unsubscribe function. */
  subscribe(listener: (s: SyncState) => void): () => void {
    this.listeners.add(listener)
    listener(this.state)
    return () => { this.listeners.delete(listener) }
  }

  getState(): SyncState {
    return this.state
  }

  private setState(patch: Partial<SyncState>): void {
    this.state = { ...this.state, ...patch }
    for (const l of this.listeners) l(this.state)
  }

  private hasOutstanding(): boolean {
    for (const slot of this.pending.values()) {
      if (slot.timer || slot.inflight || slot.waiters.length > 0) return true
    }
    return false
  }

  private recomputePending(): void {
    this.setState({ pending: this.hasOutstanding() })
  }

  /**
   * Queue a property update for `blockId`. Multiple calls within
   * DEBOUNCE_MS coalesce into a single PATCH carrying the latest props.
   * The returned promise resolves with the server's persisted block.
   */
  patch(blockId: string, properties: Record<string, unknown>): Promise<DocumentBlock> {
    let slot = this.pending.get(blockId)
    if (!slot) {
      slot = { timer: null, latest: {}, waiters: [], inflight: null }
      this.pending.set(blockId, slot)
    }
    slot.latest = { ...slot.latest, ...properties }

    if (slot.timer) clearTimeout(slot.timer)
    slot.timer = setTimeout(() => this.flushSlot(blockId), DEBOUNCE_MS)
    this.setState({ pending: true })

    return new Promise<DocumentBlock>((resolve, reject) => {
      slot!.waiters.push({ resolve, reject })
    })
  }

  private flushSlot(blockId: string): void {
    const slot = this.pending.get(blockId)
    if (!slot) return
    slot.timer = null
    const properties = slot.latest
    const waiters = slot.waiters
    slot.latest = {}
    slot.waiters = []
    if (Object.keys(properties).length === 0) return

    const p = updateBlock(this.docId, blockId, properties)
    slot.inflight = p
    p.then(
      (block) => {
        for (const w of waiters) w.resolve(block)
        this.setState({ lastSavedAt: Date.now(), lastError: null })
      },
      (err) => {
        const msg = err instanceof Error ? err.message : String(err)
        toast.error('Failed to save block')
        for (const w of waiters) w.reject(err)
        this.setState({ lastError: msg })
      },
    ).finally(() => {
      if (slot.inflight === p) slot.inflight = null
      this.recomputePending()
    })
  }

  /** Insert a new block instance. No debounce. */
  async insert(block: {
    type: string
    properties?: Record<string, unknown>
    parent_id?: string | null
    after_block_id?: string | null
    id?: string
  }): Promise<DocumentBlock> {
    try {
      const result = await insertBlock(this.docId, block)
      this.setState({ lastSavedAt: Date.now(), lastError: null })
      return result
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Failed to add block')
      this.setState({ lastError: msg })
      throw err
    }
  }

  /** Delete a block. No debounce. */
  async remove(blockId: string): Promise<void> {
    try {
      await deleteBlock(this.docId, blockId)
      this.setState({ lastSavedAt: Date.now(), lastError: null })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Failed to delete block')
      this.setState({ lastError: msg })
      throw err
    }
  }

  /**
   * Upsert the synthetic `__doc_state__` row that carries per-document
   * state (vars / order / deleted). Tries PATCH first; falls back to
   * POST when the row doesn't exist yet. Errors are toasted; the
   * promise rejects so callers can invalidate their save-cache.
   */
  async saveDocState(payload: {
    vars?: Record<string, unknown>
    order?: Record<string, string[]>
    deleted?: string[]
  }): Promise<DocumentBlock> {
    const properties: Record<string, unknown> = {}
    if (payload.vars    !== undefined) properties.vars    = payload.vars
    if (payload.order   !== undefined) properties.order   = payload.order
    if (payload.deleted !== undefined) properties.deleted = payload.deleted
    try {
      const block = await updateBlock(this.docId, DOC_STATE_BLOCK_ID, properties)
      this.setState({ lastSavedAt: Date.now(), lastError: null })
      return block
    } catch (err: unknown) {
      // 404 → the row hasn't been created yet; POST it.
      const status = (err as { response?: { status?: number }; status?: number } | null)?.response?.status
        ?? (err as { status?: number } | null)?.status
      if (status === 404) {
        try {
          const block = await insertBlock(this.docId, {
            id: DOC_STATE_BLOCK_ID,
            type: DOC_STATE_BLOCK_ID,
            parent_id: null,
            properties,
          })
          this.setState({ lastSavedAt: Date.now(), lastError: null })
          return block
        } catch (insertErr: unknown) {
          const msg = insertErr instanceof Error ? insertErr.message : String(insertErr)
          toast.error('Failed to save document layout')
          this.setState({ lastError: msg })
          throw insertErr
        }
      }
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Failed to save document layout')
      this.setState({ lastError: msg })
      throw err
    }
  }

  /** Flush all pending debounced writes immediately and await them. */
  async flush(): Promise<void> {
    const promises: Promise<unknown>[] = []
    for (const [blockId, slot] of this.pending) {
      if (slot.timer) {
        clearTimeout(slot.timer)
        this.flushSlot(blockId)
      }
      if (slot.inflight) promises.push(slot.inflight.catch(() => undefined))
    }
    await Promise.all(promises)
  }
}
